import { useEffect, useMemo, useRef, useState } from "react";
import type { SyntheticEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError, apiFetch, ensureCsrf, setCsrfToken } from "@contractor-quoting/api-client";
import {
  clearChatDraft,
  loadChatDraft,
  saveChatDraft,
  subscribeToChatDraft,
} from "../chat-draft.js";
import { AgentChatWorkspace } from "../components/AgentChatWorkspace.js";
import type { QuoteFormSubmission } from "../components/QuoteReviewModal.js";
import { Shell } from "../components/Shell.js";
import { ContractorSelector } from "../components/ContractorSelector.js";
import { CustomerWorkspaceShell } from "../components/CustomerWorkspaceShell.js";
import { ProjectDescriptionStart } from "../components/ProjectDescriptionStart.js";
import { usePendingChatReconciliation } from "../hooks/usePendingChatReconciliation.js";
import { useStructuredIntakeActions } from "../hooks/useStructuredIntakeActions.js";
import {
  clearDraftPrompt,
  readDraftPrompt,
  readDraftPromptId,
  saveDraftPrompt,
} from "../project-intake.js";
import {
  startAnonymousStructuredProject,
  startCustomerStructuredProject,
} from "../start-project-chat.js";
import type { IntakeState } from "../structured-intake-model.js";
import {
  chatSteps as steps,
  type ChatMessage,
  type ContractorPublic,
  type InitialChatState,
  type ViewerMode,
} from "../agent-chat-model.js";

interface StoredChat {
  sessionId: string | null;
  pending: boolean;
  messages: Array<{
    id: string;
    role: "user" | "assistant";
    text: string;
    state: "queued" | "running" | "completed" | "failed" | "cancelled";
  }>;
}

interface SavedEstimate {
  currency: string;
  total: { lowMinor: string; highMinor: string };
}

function toStoredChatMessages(chat: StoredChat): ChatMessage[] {
  return chat.messages.map((message) => ({
    id: message.id,
    role: message.role === "assistant" ? "agent" : "user",
    text: message.text,
    state: message.state,
  }));
}

export function AgentChatPage() {
  const { contractorSlug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const requestedJobType = searchParams.get("jobType");
  const intakeMode = searchParams.get("intake") === "1";
  const jobTypeCode = requestedJobType ?? "";
  const selectionKey = `${contractorSlug}:${intakeMode ? "intake-v2" : jobTypeCode}`;
  const navigate = useNavigate();
  const initialization = useRef<{
    slug: string;
    prompt: string;
    promise: Promise<
      InitialChatState & {
        pending?: boolean;
        jobOfferings: ContractorPublic["jobOfferings"];
        structuredIntake: IntakeState | null;
      }
    >;
  } | null>(null);
  const viewerModeRequest = useRef<Promise<Exclude<ViewerMode, "checking">> | null>(null);
  const customerCreation = useRef<{
    slug: string;
    promise: Promise<string>;
  } | null>(null);
  const promptedSlug = useRef<string | null>(null);
  const applyingStoredDraft = useRef(false);
  const conversationEnd = useRef<HTMLDivElement | null>(null);
  const [contractor, setContractor] = useState<ContractorPublic["contractor"] | null>(null);
  const [projectId, setProjectId] = useState("");
  const [, setProjectStatus] = useState("exploring");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [draftReady, setDraftReady] = useState(false);
  const [viewerMode, setViewerMode] = useState<ViewerMode>("checking");
  const [customerAttempt, setCustomerAttempt] = useState(0);
  const [startDescription, setStartDescription] = useState(readDraftPrompt);
  const [promptReady, setPromptReady] = useState(() => Boolean(readDraftPrompt()));
  const [startingIntake, setStartingIntake] = useState(false);
  const [intakeState, setIntakeState] = useState<IntakeState | null>(null);
  const [jobOfferings, setJobOfferings] = useState<ContractorPublic["jobOfferings"]>([]);
  const [classificationBusy, setClassificationBusy] = useState<string | null>(null);
  const [intakeActionBusy, setIntakeActionBusy] = useState<string | null>(null);
  const [estimateBusy, setEstimateBusy] = useState(false);
  const [savedEstimate, setSavedEstimate] = useState<SavedEstimate | null>(null);
  const {
    acceptStandardDimension,
    confirmClassification,
    confirmMeasurement,
    decideFact,
    decideObservation,
    resolveScope,
    uploadCapture,
  } = useStructuredIntakeActions({
    setClassificationBusy,
    setError,
    setIntakeActionBusy,
    setIntakeState,
  });

  useEffect(() => {
    viewerModeRequest.current ??= apiFetch<{ memberships: Array<{ id: string }> }>("/v1/auth/me")
      .then((identity) => (identity.memberships.length > 0 ? "contractor" : "customer"))
      .catch(() => "anonymous");
    let active = true;
    void viewerModeRequest.current.then((mode) => {
      if (active) setViewerMode(mode);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (viewerMode !== "customer" || !contractorSlug || !requestedJobType) return;
    if (customerCreation.current?.slug !== selectionKey) {
      customerCreation.current = {
        slug: selectionKey,
        promise: (async () => {
          await ensureCsrf();
          const created = await apiFetch<{ id: string }>("/v1/customer/projects", {
            method: "POST",
            body: JSON.stringify({ contractorSlug, jobTypeCode }),
          });
          const prompt = readDraftPrompt();
          if (prompt) {
            saveChatDraft(created.id, [], prompt);
            try {
              await apiFetch(`/v1/customer/projects/${created.id}/chat/session`, {
                method: "POST",
              });
              await apiFetch(`/v1/customer/projects/${created.id}/chat/messages`, {
                method: "POST",
                body: JSON.stringify({ message: prompt, clientMessageId: readDraftPromptId() }),
              });
              clearChatDraft(created.id);
              clearDraftPrompt();
            } catch {
              // The saved project page restores the message so the customer can retry it.
            }
          }
          return created.id;
        })(),
      };
    }
    const current = customerCreation.current;
    let active = true;
    setError("");
    void current.promise
      .then((createdProjectId) => {
        if (active) void navigate(`/projects/${createdProjectId}`, { replace: true });
      })
      .catch((reason: unknown) => {
        if (!active) return;
        if (reason instanceof ApiError && reason.code === "service_address_required") {
          const returnTo = `/chat/${encodeURIComponent(contractorSlug)}${requestedJobType ? `?jobType=${encodeURIComponent(jobTypeCode)}` : ""}`;
          void navigate(`/profile?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
          return;
        }
        setError(reason instanceof Error ? reason.message : "The project could not be created");
      });
    return () => {
      active = false;
    };
  }, [
    contractorSlug,
    customerAttempt,
    jobTypeCode,
    navigate,
    requestedJobType,
    selectionKey,
    viewerMode,
  ]);

  useEffect(() => {
    if (
      !contractorSlug ||
      (!requestedJobType && !intakeMode) ||
      viewerMode === "checking" ||
      (viewerMode === "customer" && !intakeMode)
    )
      return;
    if (initialization.current?.slug !== selectionKey) {
      const prompt = readDraftPrompt();
      initialization.current = {
        slug: selectionKey,
        prompt,
        promise: (async () => {
          const contractorResult = await apiFetch<ContractorPublic>(
            `/v1/public/contractors/${encodeURIComponent(contractorSlug)}`,
          );
          setContractor(contractorResult.contractor);
          setJobOfferings(contractorResult.jobOfferings);
          const quote = intakeMode
            ? await apiFetch<{ projectId: string; status: string }>("/v1/quote/project")
            : await apiFetch<{ projectId: string; csrfToken: string }>("/v1/quote/sessions", {
                method: "POST",
                body: JSON.stringify({ contractorSlug, jobTypeCode }),
              });
          if ("csrfToken" in quote) setCsrfToken(quote.csrfToken);
          const project = intakeMode
            ? { status: "status" in quote ? quote.status : "exploring" }
            : await apiFetch<{ status: string }>("/v1/quote/project");
          let chat: StoredChat = { sessionId: null, pending: false, messages: [] };
          let warning: string | undefined;
          try {
            await apiFetch("/v1/quote/agent/session", {
              method: "POST",
            });
            chat = await apiFetch<StoredChat>("/v1/quote/agent/chat");
          } catch (reason) {
            warning =
              reason instanceof Error
                ? reason.message
                : "The project guide is temporarily unavailable. Your project is saved.";
          }
          const storedDraft = loadChatDraft(quote.projectId);
          const structuredIntake = intakeMode
            ? await apiFetch<IntakeState>("/v2/quote/intake")
            : null;
          return {
            contractor: contractorResult.contractor,
            projectId: quote.projectId,
            projectStatus: project.status,
            messages:
              chat.messages.length > 0
                ? toStoredChatMessages(chat)
                : [
                    {
                      id: crypto.randomUUID(),
                      role: warning ? "status" : "agent",
                      text: warning
                        ? "Your project was created, but the guide could not start. Retry in a moment; your project will not be duplicated."
                        : "Your project guide is ready. Tell me what you’re planning, and we’ll take it one step at a time.",
                    },
                  ],
            input: storedDraft?.input ?? "",
            pending: chat.pending,
            jobOfferings: contractorResult.jobOfferings,
            structuredIntake,
            ...(warning ? { warning } : {}),
          };
        })(),
      };
    }
    const current = initialization.current;
    let active = true;
    void current.promise
      .then((result) => {
        if (!active) return;
        setContractor(result.contractor);
        setJobOfferings(result.jobOfferings);
        setIntakeState(result.structuredIntake);
        setSavedEstimate(result.structuredIntake?.estimate ?? null);
        setProjectId(result.projectId);
        setProjectStatus(result.projectStatus);
        setMessages(result.messages);
        setPending(result.pending ?? false);
        setInput(result.input);
        setError(result.warning ?? "");
        setDraftReady(true);
        if (current.prompt && promptedSlug.current !== contractorSlug) {
          promptedSlug.current = contractorSlug;
          const userMessage: ChatMessage = {
            id: readDraftPromptId(),
            role: "user",
            text: current.prompt,
          };
          setMessages([...result.messages, userMessage]);
          setSending(true);
          void apiFetch<StoredChat>("/v1/quote/agent/messages", {
            method: "POST",
            body: JSON.stringify({
              message: current.prompt,
              clientMessageId: userMessage.id,
            }),
          })
            .then((chat) => {
              if (active) {
                clearDraftPrompt();
                setMessages(toStoredChatMessages(chat));
                setPending(chat.pending);
                void apiFetch<{ status: string }>("/v1/quote/project")
                  .then((project) => {
                    if (active) setProjectStatus(project.status);
                  })
                  .catch(() => undefined);
              }
            })
            .catch((reason: unknown) => {
              if (active)
                setError(
                  reason instanceof Error ? reason.message : "Your first message could not be sent",
                );
            })
            .finally(() => {
              if (active) setSending(false);
            });
        }
      })
      .catch((reason: unknown) => {
        if (active)
          setError(reason instanceof Error ? reason.message : "The project guide is unavailable");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [contractorSlug, intakeMode, jobTypeCode, selectionKey, viewerMode]);

  usePendingChatReconciliation<StoredChat>({
    enabled: pending && (viewerMode === "anonymous" || intakeMode),
    endpoint: "/v1/quote/agent/reconcile",
    onResult: (chat) => {
      setMessages(toStoredChatMessages(chat));
      setPending(chat.pending);
      if (intakeMode) {
        void apiFetch<IntakeState>("/v2/quote/intake")
          .then((state) => {
            setIntakeState(state);
            setSavedEstimate(state.estimate ?? null);
          })
          .catch(() => undefined);
      }
    },
    onAuthenticationError: (reason) => {
      setPending(false);
      setError(reason.message);
    },
  });

  const startConversationalIntake = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const description = startDescription.trim();
    if (!description || startingIntake) return;
    saveDraftPrompt(description);
    setPromptReady(true);
    if (!contractorSlug) return;
    setStartingIntake(true);
    setError("");
    try {
      if (viewerMode === "customer") {
        const createdProjectId = await startCustomerStructuredProject(contractorSlug, description);
        void navigate(`/projects/${createdProjectId}`, { replace: true });
      } else {
        const destination = await startAnonymousStructuredProject(contractorSlug, description);
        void navigate(destination, { replace: true });
      }
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === "service_address_required") {
        const returnTo = `/chat/${encodeURIComponent(contractorSlug)}`;
        void navigate(`/profile?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
        return;
      }
      setError(reason instanceof Error ? reason.message : "The project intake could not start");
    } finally {
      setStartingIntake(false);
    }
  };

  useEffect(() => {
    if (!projectId || !draftReady) return;
    if (applyingStoredDraft.current) {
      applyingStoredDraft.current = false;
      return;
    }
    saveChatDraft(projectId, messages, input);
  }, [draftReady, input, messages, projectId]);

  useEffect(() => {
    if (!projectId) return;
    return subscribeToChatDraft(projectId, (draft) => {
      applyingStoredDraft.current = true;
      setMessages(draft.messages);
      setInput(draft.input);
    });
  }, [projectId]);

  useEffect(() => {
    conversationEnd.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const progress = useMemo(() => {
    const latest = messages.findLast((message) => message.total && message.completed !== undefined);
    if (!latest?.total) return 0;
    return Math.min(steps.length, Math.floor((latest.completed! / latest.total) * steps.length));
  }, [messages]);

  const saveQuoteForm = async (facts: QuoteFormSubmission[]) => {
    setEstimateBusy(true);
    setError("");
    try {
      const result = await apiFetch<{
        quoteStatus: "quoted" | "needs_information";
        requiredInformation: Array<{ message: string }>;
        quoteForm: NonNullable<IntakeState["quoteForm"]>;
        estimate: SavedEstimate | null;
      }>("/v2/quote/intake/review-quote", {
        method: "POST",
        body: JSON.stringify({ facts }),
      });
      if (!result.estimate) {
        throw new Error(
          result.requiredInformation[0]?.message ?? "One required project value is still missing.",
        );
      }
      setSavedEstimate(result.estimate);
      setIntakeState((current) =>
        current ? { ...current, quoteForm: result.quoteForm, estimate: result.estimate } : current,
      );
      void apiFetch<{ status: string }>("/v1/quote/project")
        .then((project) => setProjectStatus(project.status))
        .catch(() => undefined);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The estimate could not be prepared");
      throw reason;
    } finally {
      setEstimateBusy(false);
    }
  };

  const sendMessage = async (event?: SyntheticEvent<HTMLFormElement>, suggestedText?: string) => {
    event?.preventDefault();
    const text = (suggestedText ?? input).trim();
    if (!text || loading || sending) return;
    const userMessage: ChatMessage = { id: crypto.randomUUID(), role: "user", text };
    setMessages((current) => [...current, userMessage]);
    setInput("");
    setSending(true);
    setError("");
    try {
      const result = await apiFetch<StoredChat>("/v1/quote/agent/messages", {
        method: "POST",
        body: JSON.stringify({ message: text, clientMessageId: userMessage.id }),
      });
      setMessages(toStoredChatMessages(result));
      setPending(result.pending);
      void apiFetch<{ status: string }>("/v1/quote/project")
        .then((project) => setProjectStatus(project.status))
        .catch(() => undefined);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Your message could not be sent");
    } finally {
      setSending(false);
    }
  };

  if (viewerMode === "checking") {
    return (
      <div className="grid min-h-screen place-items-center bg-stone-50 text-sm text-stone-500">
        Opening your project workspace…
      </div>
    );
  }

  if (!contractorSlug && !promptReady) {
    const descriptionStart = (
      <ProjectDescriptionStart
        busy={startingIntake}
        description={startDescription}
        error={error}
        onChange={setStartDescription}
        onSubmit={(event) => void startConversationalIntake(event)}
      />
    );
    return viewerMode === "customer" ? (
      <CustomerWorkspaceShell>{descriptionStart}</CustomerWorkspaceShell>
    ) : (
      <Shell accountMode={viewerMode === "contractor" ? "contractor" : "public"}>
        {descriptionStart}
      </Shell>
    );
  }

  if (!contractorSlug) return <ContractorSelector mode={viewerMode} />;

  if (!requestedJobType && !intakeMode) {
    const descriptionStart = (
      <ProjectDescriptionStart
        busy={startingIntake}
        description={startDescription}
        error={error}
        onChange={setStartDescription}
        onSubmit={(event) => void startConversationalIntake(event)}
      />
    );
    if (viewerMode === "customer") {
      return <CustomerWorkspaceShell>{descriptionStart}</CustomerWorkspaceShell>;
    }
    return (
      <Shell accountMode={viewerMode === "contractor" ? "contractor" : "public"}>
        {descriptionStart}
      </Shell>
    );
  }

  if (viewerMode === "customer" && !intakeMode) {
    return (
      <CustomerWorkspaceShell>
        <div className="grid h-full place-items-center p-8 text-center">
          <div>
            <h1 className="text-2xl font-black text-emerald-950">
              {error ? "Your project could not be started" : "Creating your saved project…"}
            </h1>
            {error ? <p className="mt-3 max-w-lg text-red-700">{error}</p> : null}
            {error ? (
              <button
                className="mt-5 rounded-xl bg-emerald-800 px-5 py-3 font-bold text-white"
                onClick={() => {
                  customerCreation.current = null;
                  setCustomerAttempt((attempt) => attempt + 1);
                }}
              >
                Try again
              </button>
            ) : null}
          </div>
        </div>
      </CustomerWorkspaceShell>
    );
  }

  if (!loading && error && !contractor) {
    return (
      <Shell accountMode={viewerMode === "contractor" ? "contractor" : "public"}>
        <main className="mx-auto max-w-xl px-4 py-20 text-center">
          <h1 className="text-3xl font-black text-emerald-950">This contractor is unavailable</h1>
          <p className="mt-4 text-stone-600">
            The link may be out of date, or this contractor is not currently configured to accept
            customer projects.
          </p>
          <Link
            className="mt-7 inline-flex min-h-12 items-center rounded-xl bg-emerald-800 px-5 font-semibold text-white"
            to="/chat"
          >
            Choose another contractor
          </Link>
        </main>
      </Shell>
    );
  }

  return (
    <AgentChatWorkspace
      acceptStandardDimension={acceptStandardDimension}
      classificationBusy={classificationBusy}
      confirmClassification={confirmClassification}
      confirmMeasurement={confirmMeasurement}
      contractor={contractor}
      contractorSlug={contractorSlug}
      conversationEnd={conversationEnd}
      decideFact={decideFact}
      decideObservation={decideObservation}
      error={error}
      input={input}
      intakeActionBusy={intakeActionBusy}
      intakeMode={intakeMode}
      intakeState={intakeState}
      jobOfferings={jobOfferings}
      jobTypeCode={jobTypeCode}
      loading={loading}
      messages={messages}
      pending={pending}
      progress={progress}
      projectId={projectId}
      resolveScope={resolveScope}
      savedEstimate={savedEstimate}
      saveQuoteForm={saveQuoteForm}
      estimateBusy={estimateBusy}
      sendMessage={sendMessage}
      sending={sending}
      setInput={setInput}
      uploadCapture={uploadCapture}
      viewerMode={viewerMode}
    />
  );
}
